"use client"

import { useEffect, useState, useCallback } from "react";
import { useIdPath } from "@/contexts/IdPathProvider";
import LayoutDefault from "../../layouts/LayoutDefault";
import embedKeyService, {
    EmbedKeyItem,
    EmbedKeyDetail,
    CreateEmbedKeyData,
    UpdateEmbedKeyData,
} from "../../utils/services/embedKey";
import {
    Eye,
    Pencil,
    Trash2,
    Plus,
    Copy,
    ExternalLink,
    RefreshCw,
    X,
    Key,
    Globe,
    CheckCircle2,
    AlertCircle,
    Loader2,
    ShieldCheck,
} from "lucide-react";

interface EmbedKeyManagementProps {
    onClose: () => void;
    isSidebarOpen: boolean;
}

type Notification = { type: "success" | "error"; message: string } | null;

// --- Helper formatters ---
const formatDate = (iso: string | null | undefined) => {
    if (!iso) return "——";
    const d = new Date(iso);
    return d.toLocaleDateString("vi-VN", {
        day: "2-digit",
        month: "2-digit",
        year: "numeric",
    });
};

const formatDateTime = (iso: string | null | undefined) => {
    if (!iso) return "——";
    const d = new Date(iso);
    return d.toLocaleString("vi-VN", {
        day: "2-digit",
        month: "2-digit",
        year: "numeric",
        hour: "2-digit",
        minute: "2-digit",
    });
};

const truncateKey = (key: string, maxLen = 40) =>
    key.length > maxLen ? `${key.slice(0, maxLen)}…` : key;

// --- Modal backdrop ---
function ModalBackdrop({ onClose, children }: { onClose?: () => void; children: React.ReactNode }) {
    return (
        <div
            className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm p-4"
            onClick={(e) => { if (e.target === e.currentTarget) onClose?.(); }}
        >
            {children}
        </div>
    );
}

// --- Notification toast ---
function NotifToast({ notif, onDismiss }: { notif: Notification; onDismiss: () => void }) {
    if (!notif) return null;
    const isSuccess = notif.type === "success";
    return (
        <div
            className={`fixed top-5 right-5 z-[100] flex items-center gap-3 px-4 py-3 rounded-xl shadow-xl text-white text-sm font-medium transition-all ${
                isSuccess ? "bg-emerald-500" : "bg-red-500"
            }`}
        >
            {isSuccess ? <CheckCircle2 size={18} /> : <AlertCircle size={18} />}
            <span>{notif.message}</span>
            <button onClick={onDismiss} className="ml-2 opacity-70 hover:opacity-100">
                <X size={14} />
            </button>
        </div>
    );
}

export default function EmbedKeyManagement({ onClose, isSidebarOpen }: EmbedKeyManagementProps) {
    const { idPath, namePath } = useIdPath();

    // ── List state ──
    const [embedKeys, setEmbedKeys] = useState<EmbedKeyItem[]>([]);
    const [isLoading, setIsLoading] = useState(false);

    // ── Notification ──
    const [notification, setNotification] = useState<Notification>(null);
    const showNotif = (type: "success" | "error", message: string) => {
        setNotification({ type, message });
        setTimeout(() => setNotification(null), 3500);
    };

    // ── Create Modal ──
    const [showCreateModal, setShowCreateModal] = useState(false);
    const [createForm, setCreateForm] = useState<CreateEmbedKeyData>({ name: "", description: "", expires_at: "" });
    const [isCreating, setIsCreating] = useState(false);

    // ── Result Modal (after create) ──
    const [showResultModal, setShowResultModal] = useState(false);
    const [newlyCreatedKey, setNewlyCreatedKey] = useState<EmbedKeyItem | null>(null);

    // ── Detail Modal ──
    const [showDetailModal, setShowDetailModal] = useState(false);
    const [selectedKeyDetail, setSelectedKeyDetail] = useState<EmbedKeyDetail | null>(null);
    const [isLoadingDetail, setIsLoadingDetail] = useState(false);

    // ── Edit Modal ──
    const [showEditModal, setShowEditModal] = useState(false);
    const [editForm, setEditForm] = useState<UpdateEmbedKeyData>({});
    const [editingKey, setEditingKey] = useState<EmbedKeyItem | null>(null);
    const [isUpdating, setIsUpdating] = useState(false);

    // ── Domain Modal ──
    const [showSetDomainModal, setShowSetDomainModal] = useState(false);
    const [domainInput, setDomainInput] = useState("");
    const [isSettingDomain, setIsSettingDomain] = useState(false);

    // ═══════════════════════════════════════════════
    //  Data Fetching
    // ═══════════════════════════════════════════════

    const fetchKeys = useCallback(async () => {
        if (!idPath) return;
        setIsLoading(true);
        try {
            const res = await embedKeyService.list(idPath, namePath);
            setEmbedKeys((res as any)?.data?.data || []);
        } catch {
            showNotif("error", "Lỗi khi tải danh sách embed keys");
        } finally {
            setIsLoading(false);
        }
    }, [idPath, namePath]);

    useEffect(() => {
        fetchKeys();
    }, [fetchKeys]);

    // ═══════════════════════════════════════════════
    //  Clipboard helper
    // ═══════════════════════════════════════════════

    const copyToClipboard = async (text: string, label: string) => {
        try {
            await navigator.clipboard.writeText(text);
            showNotif("success", `Đã copy ${label}!`);
        } catch {
            showNotif("error", "Không thể copy. Vui lòng copy thủ công.");
        }
    };

    // ═══════════════════════════════════════════════
    //  Create
    // ═══════════════════════════════════════════════

    const handleCreateKey = async () => {
        if (!createForm.name.trim()) {
            showNotif("error", "Tên key không được để trống");
            return;
        }
        setIsCreating(true);
        try {
            const body: CreateEmbedKeyData = { name: createForm.name.trim() };
            if (createForm.description?.trim()) body.description = createForm.description.trim();
            if (createForm.expires_at) body.expires_at = new Date(createForm.expires_at).toISOString();

            const res = await embedKeyService.create(idPath, body);
            const created: EmbedKeyItem = (res as any)?.data?.data;
            setNewlyCreatedKey(created);
            setShowCreateModal(false);
            setShowResultModal(true);
            setCreateForm({ name: "", description: "", expires_at: "" });
        } catch {
            showNotif("error", "Lỗi khi tạo embed key");
        } finally {
            setIsCreating(false);
        }
    };

    // ═══════════════════════════════════════════════
    //  Delete
    // ═══════════════════════════════════════════════

    const handleDeleteKey = async (tokenId: number, keyName: string) => {
        if (!window.confirm(`Bạn có chắc muốn xóa key "${keyName}"? Hành động này không thể hoàn tác.`)) return;
        try {
            await embedKeyService.deleteKey(idPath, tokenId);
            showNotif("success", `Đã xóa key "${keyName}"`);
            fetchKeys();
        } catch {
            showNotif("error", "Lỗi khi xóa key");
        }
    };

    // ═══════════════════════════════════════════════
    //  View Detail  (Phase 4)
    // ═══════════════════════════════════════════════

    const handleViewKey = async (tokenId: number) => {
        setIsLoadingDetail(true);
        setShowDetailModal(true);
        setSelectedKeyDetail(null);
        try {
            const res = await embedKeyService.getById(idPath, tokenId, namePath);
            setSelectedKeyDetail((res as any)?.data?.data || null);
        } catch {
            showNotif("error", "Lỗi khi tải chi tiết key");
            setShowDetailModal(false);
        } finally {
            setIsLoadingDetail(false);
        }
    };

    // ═══════════════════════════════════════════════
    //  Edit  (Phase 4)
    // ═══════════════════════════════════════════════

    const handleEditKey = (key: EmbedKeyItem) => {
        setEditingKey(key);
        setEditForm({
            name: key.name,
            description: key.description || "",
            expires_at: key.expires_at ? key.expires_at.split("T")[0] : "",
        });
        setShowEditModal(true);
    };

    const handleUpdateKey = async () => {
        if (!editingKey || !editForm.name?.trim()) {
            showNotif("error", "Tên key không được để trống");
            return;
        }
        setIsUpdating(true);
        try {
            const body: UpdateEmbedKeyData = { name: editForm.name?.trim() };
            if (editForm.description !== undefined) body.description = editForm.description;
            if (editForm.expires_at) body.expires_at = new Date(editForm.expires_at).toISOString();

            await embedKeyService.update(idPath, editingKey.token_id, body);
            showNotif("success", "Cập nhật key thành công!");
            setShowEditModal(false);
            fetchKeys();
        } catch {
            showNotif("error", "Lỗi khi cập nhật key");
        } finally {
            setIsUpdating(false);
        }
    };

    // ═══════════════════════════════════════════════
    //  Domain Management  (Phase 5)
    // ═══════════════════════════════════════════════

    const handleSetDomain = async () => {
        if (!selectedKeyDetail || !domainInput.trim()) return;
        if (domainInput.includes("://")) {
            showNotif("error", "Không nhập protocol (http://, https://). Chỉ nhập hostname.");
            return;
        }
        if (domainInput.endsWith("/")) {
            showNotif("error", "Không nhập dấu / ở cuối domain.");
            return;
        }
        setIsSettingDomain(true);
        try {
            await embedKeyService.setDomain(idPath, selectedKeyDetail.token_id, domainInput.trim());
            showNotif("success", "Gán domain thành công!");
            setShowSetDomainModal(false);
            setDomainInput("");
            handleViewKey(selectedKeyDetail.token_id);
        } catch {
            showNotif("error", "Lỗi khi gán domain");
        } finally {
            setIsSettingDomain(false);
        }
    };

    const handleResetDomain = async () => {
        if (!selectedKeyDetail) return;
        if (!window.confirm("Bạn có chắc muốn reset domain binding? Lần nhúng tiếp theo sẽ tự đăng ký domain mới.")) return;
        try {
            await embedKeyService.deleteDomain(idPath, selectedKeyDetail.token_id);
            showNotif("success", "Reset domain thành công!");
            handleViewKey(selectedKeyDetail.token_id);
        } catch {
            showNotif("error", "Lỗi khi reset domain");
        }
    };

    // ═══════════════════════════════════════════════
    //  Render
    // ═══════════════════════════════════════════════

    return (
        <LayoutDefault onClose={onClose} isSidebarOpen={isSidebarOpen}>
            <NotifToast notif={notification} onDismiss={() => setNotification(null)} />

            {/* ── Header ── */}
            <div className="sticky top-0 z-10 flex items-center justify-between px-6 py-4 bg-white border-b border-gray-100 shadow-sm">
                <div className="flex items-center gap-3">
                    <span className="flex items-center justify-center w-9 h-9 rounded-xl bg-indigo-100 text-indigo-600">
                        <Key size={18} />
                    </span>
                    <div>
                        <h1 className="text-2xl font-bold text-gray-900">Quản lý khóa nhúng (Embed Key)</h1>
                        <p className="text-xs text-gray-400">Quản lý khóa nhúng cho dự án</p>
                    </div>
                </div>
                <div className="flex gap-2">
                    <button
                        onClick={fetchKeys}
                        disabled={isLoading}
                        className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg border border-gray-200 text-sm text-gray-600 hover:bg-gray-50 transition-colors disabled:opacity-50"
                    >
                        <RefreshCw size={14} className={isLoading ? "animate-spin" : ""} />
                        Làm mới
                    </button>
                    <button
                        onClick={() => setShowCreateModal(true)}
                        className="flex items-center gap-1.5 px-4 py-1.5 rounded-lg bg-indigo-600 text-white text-sm font-medium hover:bg-indigo-700 transition-colors shadow-sm"
                    >
                        <Plus size={16} />
                        Tạo khóa nhúng
                    </button>
                </div>
            </div>

            {/* ── Table / Body ── */}
            <div className="p-6">
                {isLoading ? (
                    <div className="flex flex-col items-center justify-center py-20 gap-3 text-gray-400">
                        <Loader2 size={36} className="animate-spin text-indigo-400" />
                        <p className="text-sm">Đang tải danh sách embed keys…</p>
                    </div>
                ) : embedKeys.length === 0 ? (
                    <div className="flex flex-col items-center justify-center py-20 gap-4 text-gray-400">
                        <div className="w-16 h-16 rounded-full bg-gray-100 flex items-center justify-center">
                            <Key size={32} className="text-gray-300" />
                        </div>
                        <div className="text-center">
                            <p className="font-medium text-gray-500">Chưa có embed key nào</p>
                            <p className="text-sm mt-1">Tạo key đầu tiên để bắt đầu nhúng dự án vào website.</p>
                        </div>
                        <button
                            onClick={() => setShowCreateModal(true)}
                            className="flex items-center gap-2 px-4 py-2 rounded-lg bg-indigo-600 text-white text-sm font-medium hover:bg-indigo-700 transition-colors"
                        >
                            <Plus size={16} />
                            Tạo khóa nhúng
                        </button>
                    </div>
                ) : (
                    <div className="overflow-x-auto rounded-xl border border-gray-100 shadow-sm">
                        <table className="w-full text-sm">
                            <thead className="bg-gray-50">
                                <tr>
                                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wide w-10">#</th>
                                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wide">Tên key</th>
                                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wide">Domain</th>
                                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wide">Hết hạn</th>
                                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wide">Tạo lúc</th>
                                    <th className="px-4 py-3 text-right text-xs font-semibold text-gray-500 uppercase tracking-wide">Thao tác</th>
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-gray-50 bg-white">
                                {embedKeys.map((key, idx) => (
                                    <tr key={key.token_id} className="hover:bg-indigo-50/30 transition-colors">
                                        <td className="px-4 py-3 text-gray-400 font-mono text-xs">{idx + 1}</td>
                                        <td className="px-4 py-3">
                                            <div className="font-medium text-gray-800">{key.name}</div>
                                            {key.description && (
                                                <div className="text-xs text-gray-400 mt-0.5 truncate max-w-[200px]">{key.description}</div>
                                            )}
                                        </td>
                                        <td className="px-4 py-3">
                                            {key.domain ? (
                                                <div>
                                                    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs bg-emerald-50 text-emerald-700 font-medium">
                                                        <Globe size={10} />
                                                        {key.domain}
                                                    </span>
                                                    {key.registered_at && (
                                                        <div className="text-xs text-gray-400 mt-0.5">{formatDate(key.registered_at)}</div>
                                                    )}
                                                </div>
                                            ) : (
                                                <span className="text-gray-400 text-xs italic">Chưa gán</span>
                                            )}
                                        </td>
                                        <td className="px-4 py-3">
                                            {key.expires_at ? (
                                                <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs bg-amber-50 text-amber-700 font-medium">
                                                    {formatDate(key.expires_at)}
                                                </span>
                                            ) : (
                                                <span className="text-gray-400 text-xs">Vĩnh viễn</span>
                                            )}
                                        </td>
                                        <td className="px-4 py-3 text-gray-400 text-xs">{formatDate(key.created_at)}</td>
                                        <td className="px-4 py-3">
                                            <div className="flex items-center justify-end gap-1">
                                                <button
                                                    onClick={() => handleViewKey(key.token_id)}
                                                    title="Xem chi tiết"
                                                    className="p-1.5 rounded-lg text-gray-400 hover:text-indigo-600 hover:bg-indigo-50 transition-colors"
                                                >
                                                    <Eye size={15} />
                                                </button>
                                                <button
                                                    onClick={() => handleEditKey(key)}
                                                    title="Chỉnh sửa"
                                                    className="p-1.5 rounded-lg text-gray-400 hover:text-emerald-600 hover:bg-emerald-50 transition-colors"
                                                >
                                                    <Pencil size={15} />
                                                </button>
                                                <button
                                                    onClick={() => handleDeleteKey(key.token_id, key.name)}
                                                    title="Xóa"
                                                    className="p-1.5 rounded-lg text-gray-400 hover:text-red-600 hover:bg-red-50 transition-colors"
                                                >
                                                    <Trash2 size={15} />
                                                </button>
                                            </div>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                        <div className="px-4 py-2 bg-gray-50 border-t border-gray-100 text-xs text-gray-400">
                            Hiển thị {embedKeys.length} / {embedKeys.length} keys
                        </div>
                    </div>
                )}
            </div>

            {/* ══════════════════════════════════════════
                Create Modal  (Phase 3)
            ══════════════════════════════════════════ */}
            {showCreateModal && (
                <ModalBackdrop onClose={() => setShowCreateModal(false)}>
                    <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md p-6">
                        <div className="flex items-center justify-between mb-5">
                            <h2 className="text-base font-bold text-gray-800 flex items-center gap-2">
                                <Plus size={18} className="text-indigo-600" />
                                Tạo khóa nhúng (Embed Key)
                            </h2>
                            <button onClick={() => setShowCreateModal(false)} className="text-gray-400 hover:text-gray-600">
                                <X size={20} />
                            </button>
                        </div>

                        <div className="space-y-4">
                            <div>
                                <label className="block text-xs font-medium text-gray-600 mb-1.5">
                                    Tên key <span className="text-red-500">*</span>
                                </label>
                                <input
                                    type="text"
                                    value={createForm.name}
                                    onChange={(e) => setCreateForm((f) => ({ ...f, name: e.target.value }))}
                                    placeholder="Ví dụ: Production Site"
                                    className="w-full px-3 py-2 rounded-lg border border-gray-200 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                                    autoFocus
                                />
                            </div>
                            <div>
                                <label className="block text-xs font-medium text-gray-600 mb-1.5">Mô tả (tùy chọn)</label>
                                <input
                                    type="text"
                                    value={createForm.description}
                                    onChange={(e) => setCreateForm((f) => ({ ...f, description: e.target.value }))}
                                    placeholder="Mô tả ngắn về mục đích key này"
                                    className="w-full px-3 py-2 rounded-lg border border-gray-200 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                                />
                            </div>
                            <div>
                                <label className="block text-xs font-medium text-gray-600 mb-1.5">Hết hạn (tùy chọn)</label>
                                <input
                                    type="date"
                                    value={createForm.expires_at}
                                    onChange={(e) => setCreateForm((f) => ({ ...f, expires_at: e.target.value }))}
                                    className="w-full px-3 py-2 rounded-lg border border-gray-200 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                                />
                                <p className="text-xs text-gray-400 mt-1">Bỏ trống để key không giới hạn thời gian.</p>
                            </div>
                        </div>

                        <div className="flex justify-end gap-2 mt-6">
                            <button
                                onClick={() => setShowCreateModal(false)}
                                className="px-4 py-2 rounded-lg border border-gray-200 text-sm text-gray-600 hover:bg-gray-50 transition-colors"
                            >
                                Hủy bỏ
                            </button>
                            <button
                                onClick={handleCreateKey}
                                disabled={isCreating}
                                className="px-4 py-2 rounded-lg bg-indigo-600 text-white text-sm font-medium hover:bg-indigo-700 transition-colors disabled:opacity-50 flex items-center gap-2"
                            >
                                {isCreating && <Loader2 size={14} className="animate-spin" />}
                                Tạo khóa nhúng
                            </button>
                        </div>
                    </div>
                </ModalBackdrop>
            )}

            {/* ══════════════════════════════════════════
                Result Modal  (Phase 3)
            ══════════════════════════════════════════ */}
            {showResultModal && newlyCreatedKey && (
                <ModalBackdrop>
                    <div className="bg-white rounded-2xl shadow-2xl w-full max-w-lg p-6">
                        <div className="flex items-center justify-between mb-4">
                            <h2 className="text-base font-bold text-emerald-700 flex items-center gap-2">
                                <CheckCircle2 size={20} className="text-emerald-500" />
                                Tạo key thành công!
                            </h2>
                            <button onClick={() => { setShowResultModal(false); fetchKeys(); }} className="text-gray-400 hover:text-gray-600">
                                <X size={20} />
                            </button>
                        </div>

                        <div className="bg-amber-50 border border-amber-200 rounded-xl px-4 py-3 mb-4 flex gap-2">
                            <AlertCircle size={16} className="text-amber-600 mt-0.5 shrink-0" />
                            <p className="text-xs text-amber-700 font-medium">
                                Lưu composite key ngay! Key này sẽ <strong>không hiển thị lại</strong> sau khi đóng modal.
                            </p>
                        </div>

                        <div className="space-y-4">
                            {/* Composite Key */}
                            <div>
                                <label className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1.5 block">
                                    Composite Key
                                </label>
                                <div className="flex items-center gap-2 bg-gray-50 rounded-xl p-3">
                                    <code className="flex-1 text-xs font-mono text-gray-700 break-all select-all">
                                        {newlyCreatedKey.composite_key}
                                    </code>
                                    <button
                                        onClick={() => copyToClipboard(newlyCreatedKey.composite_key, "composite key")}
                                        className="shrink-0 p-1.5 rounded-lg text-indigo-600 hover:bg-indigo-50 transition-colors"
                                        title="Copy"
                                    >
                                        <Copy size={14} />
                                    </button>
                                </div>
                            </div>

                            {/* Embed URL */}
                            {newlyCreatedKey.embed_url && (
                                <div>
                                    <label className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1.5 block">
                                        Embed URL
                                    </label>
                                    <div className="flex items-center gap-2 bg-gray-50 rounded-xl p-3">
                                        <code className="flex-1 text-xs font-mono text-gray-700 break-all select-all">
                                            {newlyCreatedKey.embed_url}
                                        </code>
                                        <div className="flex gap-1 shrink-0">
                                            <button
                                                onClick={() => copyToClipboard(newlyCreatedKey.embed_url!, "embed URL")}
                                                className="p-1.5 rounded-lg text-indigo-600 hover:bg-indigo-50 transition-colors"
                                                title="Copy"
                                            >
                                                <Copy size={14} />
                                            </button>
                                            <button
                                                onClick={() => window.open(newlyCreatedKey.embed_url, "_blank")}
                                                className="p-1.5 rounded-lg text-gray-500 hover:bg-gray-100 transition-colors"
                                                title="Xem thử"
                                            >
                                                <ExternalLink size={14} />
                                            </button>
                                        </div>
                                    </div>
                                </div>
                            )}

                            {/* iFrame Snippet */}
                            {newlyCreatedKey.iframe_snippet && (
                                <div>
                                    <label className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1.5 block">
                                        iFrame Snippet
                                    </label>
                                    <div className="flex items-start gap-2 bg-gray-50 rounded-xl p-3">
                                        <code className="flex-1 text-xs font-mono text-gray-700 break-all whitespace-pre-wrap select-all">
                                            {newlyCreatedKey.iframe_snippet}
                                        </code>
                                        <button
                                            onClick={() => copyToClipboard(newlyCreatedKey.iframe_snippet!, "iframe snippet")}
                                            className="shrink-0 p-1.5 rounded-lg text-indigo-600 hover:bg-indigo-50 transition-colors"
                                            title="Copy"
                                        >
                                            <Copy size={14} />
                                        </button>
                                    </div>
                                </div>
                            )}
                        </div>

                        <div className="flex justify-end mt-6">
                            <button
                                onClick={() => { setShowResultModal(false); fetchKeys(); }}
                                className="px-5 py-2 rounded-lg bg-indigo-600 text-white text-sm font-medium hover:bg-indigo-700 transition-colors"
                            >
                                Đóng &amp; Refresh
                            </button>
                        </div>
                    </div>
                </ModalBackdrop>
            )}

            {/* ══════════════════════════════════════════
                Edit Modal  (Phase 4)
            ══════════════════════════════════════════ */}
            {showEditModal && editingKey && (
                <ModalBackdrop onClose={() => setShowEditModal(false)}>
                    <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md p-6">
                        <div className="flex items-center justify-between mb-5">
                            <h2 className="text-base font-bold text-gray-800 flex items-center gap-2">
                                <Pencil size={16} className="text-emerald-600" />
                                Chỉnh sửa khóa nhúng
                            </h2>
                            <button onClick={() => setShowEditModal(false)} className="text-gray-400 hover:text-gray-600">
                                <X size={20} />
                            </button>
                        </div>

                        <div className="space-y-4">
                            <div>
                                <label className="block text-xs font-medium text-gray-600 mb-1.5">
                                    Tên key <span className="text-red-500">*</span>
                                </label>
                                <input
                                    type="text"
                                    value={editForm.name}
                                    onChange={(e) => setEditForm((f) => ({ ...f, name: e.target.value }))}
                                    className="w-full px-3 py-2 rounded-lg border border-gray-200 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
                                    autoFocus
                                />
                            </div>
                            <div>
                                <label className="block text-xs font-medium text-gray-600 mb-1.5">Mô tả</label>
                                <input
                                    type="text"
                                    value={editForm.description}
                                    onChange={(e) => setEditForm((f) => ({ ...f, description: e.target.value }))}
                                    className="w-full px-3 py-2 rounded-lg border border-gray-200 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
                                />
                            </div>
                            <div>
                                <label className="block text-xs font-medium text-gray-600 mb-1.5">Hết hạn</label>
                                <input
                                    type="date"
                                    value={editForm.expires_at}
                                    onChange={(e) => setEditForm((f) => ({ ...f, expires_at: e.target.value }))}
                                    className="w-full px-3 py-2 rounded-lg border border-gray-200 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
                                />
                            </div>
                        </div>

                        <div className="flex justify-end gap-2 mt-6">
                            <button
                                onClick={() => setShowEditModal(false)}
                                className="px-4 py-2 rounded-lg border border-gray-200 text-sm text-gray-600 hover:bg-gray-50 transition-colors"
                            >
                                Hủy bỏ
                            </button>
                            <button
                                onClick={handleUpdateKey}
                                disabled={isUpdating}
                                className="px-4 py-2 rounded-lg bg-emerald-600 text-white text-sm font-medium hover:bg-emerald-700 transition-colors disabled:opacity-50 flex items-center gap-2"
                            >
                                {isUpdating && <Loader2 size={14} className="animate-spin" />}
                                Lưu thay đổi
                            </button>
                        </div>
                    </div>
                </ModalBackdrop>
            )}

            {/* ══════════════════════════════════════════
                Detail Modal  (Phase 4 + 5)
            ══════════════════════════════════════════ */}
            {showDetailModal && (
                <ModalBackdrop onClose={() => setShowDetailModal(false)}>
                    <div className="bg-white rounded-2xl shadow-2xl w-full max-w-xl p-6 max-h-[90vh] overflow-y-auto">
                        <div className="flex items-center justify-between mb-5">
                            <h2 className="text-base font-bold text-gray-800 flex items-center gap-2">
                                <ShieldCheck size={18} className="text-indigo-600" />
                                Chi tiết khóa nhúng{selectedKeyDetail ? `: "${selectedKeyDetail.name}"` : ""}
                            </h2>
                            <button onClick={() => setShowDetailModal(false)} className="text-gray-400 hover:text-gray-600">
                                <X size={20} />
                            </button>
                        </div>

                        {isLoadingDetail ? (
                            <div className="flex flex-col items-center justify-center py-16 gap-3 text-gray-400">
                                <Loader2 size={30} className="animate-spin text-indigo-400" />
                                <p className="text-sm">Đang tải chi tiết…</p>
                            </div>
                        ) : selectedKeyDetail ? (
                            <div className="space-y-5">
                                {/* Info block */}
                                <div className="bg-gray-50 rounded-xl p-4 space-y-2 text-sm">
                                    {[
                                        ["Token ID", selectedKeyDetail.token_id],
                                        ["Tên", selectedKeyDetail.name],
                                        ["Mô tả", selectedKeyDetail.description || "——"],
                                        ["Hết hạn", formatDate(selectedKeyDetail.expires_at)],
                                        ["Tạo lúc", formatDateTime(selectedKeyDetail.created_at)],
                                    ].map(([label, val]) => (
                                        <div key={String(label)} className="flex items-start gap-3">
                                            <span className="text-gray-400 w-24 shrink-0 text-xs font-medium">{label}</span>
                                            <span className="text-gray-700">{String(val)}</span>
                                        </div>
                                    ))}
                                </div>

                                {/* Composite Key */}
                                <div>
                                    <label className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1.5 block">Composite Key</label>
                                    <div className="flex items-center gap-2 bg-gray-50 rounded-xl p-3">
                                        <code
                                            className="flex-1 text-xs font-mono text-gray-700 break-all select-all"
                                            title={selectedKeyDetail.composite_key}
                                        >
                                            {truncateKey(selectedKeyDetail.composite_key, 55)}
                                        </code>
                                        <button
                                            onClick={() => copyToClipboard(selectedKeyDetail.composite_key, "composite key")}
                                            className="shrink-0 p-1.5 rounded-lg text-indigo-600 hover:bg-indigo-50 transition-colors"
                                        >
                                            <Copy size={14} />
                                        </button>
                                    </div>
                                </div>

                                {/* Token (raw hex) */}
                                {/* <div>
                                    <label className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1.5 block">Token</label>
                                    <div className="flex items-center gap-2 bg-gray-50 rounded-xl p-3">
                                        <code
                                            className="flex-1 text-xs font-mono text-gray-600 break-all select-all"
                                            title={selectedKeyDetail.token}
                                        >
                                            {truncateKey(selectedKeyDetail.token, 55)}
                                        </code>
                                        <button
                                            onClick={() => copyToClipboard(selectedKeyDetail.token, "token")}
                                            className="shrink-0 p-1.5 rounded-lg text-gray-400 hover:text-indigo-600 hover:bg-indigo-50 transition-colors"
                                        >
                                            <Copy size={14} />
                                        </button>
                                    </div>
                                </div> */}

                                {/* Embed URL */}
                                {selectedKeyDetail.embed_url && (
                                    <div>
                                        <label className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1.5 block">Embed URL</label>
                                        <div className="flex items-center gap-2 bg-gray-50 rounded-xl p-3">
                                            <code className="flex-1 text-xs font-mono text-gray-700 break-all select-all">{selectedKeyDetail.embed_url}</code>
                                            <div className="flex gap-1 shrink-0">
                                                <button onClick={() => copyToClipboard(selectedKeyDetail.embed_url!, "embed URL")} className="p-1.5 rounded-lg text-indigo-600 hover:bg-indigo-50 transition-colors"><Copy size={14} /></button>
                                                <button onClick={() => window.open(selectedKeyDetail.embed_url, "_blank")} className="p-1.5 rounded-lg text-gray-500 hover:bg-gray-100 transition-colors"><ExternalLink size={14} /></button>
                                            </div>
                                        </div>
                                    </div>
                                )}

                                {/* iFrame Snippet */}
                                {selectedKeyDetail.iframe_snippet && (
                                    <div>
                                        <label className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1.5 block">iFrame Snippet</label>
                                        <div className="flex items-start gap-2 bg-gray-50 rounded-xl p-3">
                                            <code className="flex-1 text-xs font-mono text-gray-700 break-all whitespace-pre-wrap select-all">{selectedKeyDetail.iframe_snippet}</code>
                                            <button onClick={() => copyToClipboard(selectedKeyDetail.iframe_snippet!, "iframe snippet")} className="shrink-0 p-1.5 rounded-lg text-indigo-600 hover:bg-indigo-50 transition-colors"><Copy size={14} /></button>
                                        </div>
                                    </div>
                                )}

                                {/* ── Domain Binding (Phase 5) ── */}
                                <div className="border-t border-gray-100 pt-4">
                                    <div className="flex items-center gap-2 mb-3">
                                        <Globe size={15} className="text-indigo-500" />
                                        <span className="text-xs font-semibold text-gray-600 uppercase tracking-wide">Domain Binding</span>
                                    </div>

                                    {selectedKeyDetail.domain ? (
                                        <div className="bg-indigo-50 rounded-xl p-4">
                                            <div className="flex items-center justify-between mb-1">
                                                <span className="font-medium text-indigo-800 text-sm">{selectedKeyDetail.domain}</span>
                                            </div>
                                            {selectedKeyDetail.registered_at && (
                                                <p className="text-xs text-indigo-500">Đăng ký: {formatDateTime(selectedKeyDetail.registered_at)}</p>
                                            )}
                                            <div className="flex gap-2 mt-3">
                                                <button
                                                    onClick={() => { setDomainInput(selectedKeyDetail.domain || ""); setShowSetDomainModal(true); }}
                                                    className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs bg-white border border-indigo-200 text-indigo-700 hover:bg-indigo-100 transition-colors font-medium"
                                                >
                                                    <RefreshCw size={12} />
                                                    Đổi domain
                                                </button>
                                                <button
                                                    onClick={handleResetDomain}
                                                    className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs bg-white border border-red-200 text-red-600 hover:bg-red-50 transition-colors font-medium"
                                                >
                                                    <Trash2 size={12} />
                                                    Reset binding
                                                </button>
                                            </div>
                                        </div>
                                    ) : (
                                        <div className="bg-gray-50 rounded-xl p-4">
                                            <p className="text-sm text-gray-500 mb-1">ℹ️ Chưa có domain binding.</p>
                                            <p className="text-xs text-gray-400 mb-3">Khách hàng nhúng lần đầu sẽ tự đăng ký domain.</p>
                                            <button
                                                onClick={() => { setDomainInput(""); setShowSetDomainModal(true); }}
                                                className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs bg-indigo-600 text-white hover:bg-indigo-700 transition-colors font-medium"
                                            >
                                                <Globe size={12} />
                                                Gán domain thủ công
                                            </button>
                                        </div>
                                    )}
                                </div>
                            </div>
                        ) : (
                            <div className="text-center py-10 text-gray-400 text-sm">Không tải được thông tin key.</div>
                        )}

                        <div className="flex justify-end mt-6">
                            <button onClick={() => setShowDetailModal(false)} className="px-4 py-2 rounded-lg border border-gray-200 text-sm text-gray-600 hover:bg-gray-50 transition-colors">
                                Đóng
                            </button>
                        </div>
                    </div>
                </ModalBackdrop>
            )}

            {/* ══════════════════════════════════════════
                Set Domain Modal  (Phase 5)
            ══════════════════════════════════════════ */}
            {showSetDomainModal && (
                <ModalBackdrop onClose={() => setShowSetDomainModal(false)}>
                    <div className="bg-white rounded-2xl shadow-2xl w-full max-w-sm p-6 z-[60]">
                        <div className="flex items-center justify-between mb-4">
                            <h2 className="text-base font-bold text-gray-800 flex items-center gap-2">
                                <Globe size={16} className="text-indigo-600" />
                                Gán Domain
                            </h2>
                            <button onClick={() => setShowSetDomainModal(false)} className="text-gray-400 hover:text-gray-600">
                                <X size={20} />
                            </button>
                        </div>

                        <div>
                            <label className="block text-xs font-medium text-gray-600 mb-1.5">
                                Domain <span className="text-red-500">*</span>
                            </label>
                            <input
                                type="text"
                                value={domainInput}
                                onChange={(e) => setDomainInput(e.target.value)}
                                placeholder="customer-site.com"
                                className="w-full px-3 py-2 rounded-lg border border-gray-200 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
                                autoFocus
                            />
                            <p className="text-xs text-gray-400 mt-1.5">Ví dụ: <code>customer-site.com</code> — Không nhập http:// hoặc dấu /</p>
                        </div>

                        <div className="flex justify-end gap-2 mt-5">
                            <button onClick={() => setShowSetDomainModal(false)} className="px-4 py-2 rounded-lg border border-gray-200 text-sm text-gray-600 hover:bg-gray-50 transition-colors">
                                Hủy
                            </button>
                            <button
                                onClick={handleSetDomain}
                                disabled={isSettingDomain || !domainInput.trim()}
                                className="px-4 py-2 rounded-lg bg-indigo-600 text-white text-sm font-medium hover:bg-indigo-700 transition-colors disabled:opacity-50 flex items-center gap-2"
                            >
                                {isSettingDomain && <Loader2 size={14} className="animate-spin" />}
                                Xác nhận
                            </button>
                        </div>
                    </div>
                </ModalBackdrop>
            )}
        </LayoutDefault>
    );
}
